<template>
  <div ref="defineTab">
    <panel-block v-if="selectedObjectModelTrigger.can(ObjectModelTriggerCapability.labelable)" id="pn-name">
      <panel-item>
        <label :for="selectedObjectModelTrigger.idForProp('label')">Trigger Name</label>
        <expandable-textarea
          :id="selectedObjectModelTrigger.idForProp('label')"
          v-model="selectedObjectModelTrigger.label_title"
          placeholder="Enter Event name..."></expandable-textarea>
      </panel-item>
    </panel-block>
    <panel-block v-if="selectedObjectModelTrigger.can(ObjectModelTriggerCapability.triggerRunnableFor)">
      <panel-item>
        <label :for="selectedObjectModelTrigger.idForProp('trigger_runs_for_type')">
          Trigger runs for
        </label>
        <a-select
          :id="selectedObjectModelTrigger.idForProp('trigger_runs_for_type')"
          compress
          v-model="selectedObjectModelTrigger.trigger_runs_for_type"
          :options="objectModelTriggerRunsForOptions"
          @input="updateTriggerBasedOn"></a-select>
      </panel-item>
    </panel-block>
    <panel-block>
      <panel-item>
        <label :for="selectedObjectModelTrigger.idForProp('trigger_based_on_type')">
          Trigger based on
          <help-tooltip v-if="selectedObjectModelTrigger.is_published_time_trigger">changing this after publishing is currently not supported</help-tooltip>
        </label>
        <a-select
          :id="selectedObjectModelTrigger.idForProp('trigger_based_on_type')"
          compress
          v-model="selectedObjectModelTrigger.trigger_based_on_type"
          :disabled="selectedObjectModelTrigger.is_published_time_trigger"
          :options="objectModelTriggerBasedOnOptions"></a-select>
      </panel-item>
    </panel-block>

    <panel-block v-if="selectedObjectModelTrigger.trigger_based_on_type === ObjectModelTriggerBasedOnType.TimeBasedEvent">
      <panel-item>
        <conditional-wrapper
          :parent="selectedObjectModelTrigger"
          :field-definition="ObjectModelConditionalFields.TIME_TRIGGER_AVAILABLE"
          :has-otherwise-value="false"
          :can-add-rule="Object.keys(selectedObjectModelTrigger.time_trigger_conditional_rule_sets).length === 0"
          :validation-model="selectedObjectModelTrigger.parent"
          :validation-field-name="`triggerConditional-${selectedObjectModelTrigger.client_id}`"
        >
          <template #header>
            Conditional
            <help-tooltip>Please read the documentation on how this works.</help-tooltip>
          </template>

          <template #value="slotProps">
            <input
              type="hidden"
              v-model="slotProps.model[slotProps.fieldName]"
              value="true"
            >
          </template>
        </conditional-wrapper>
      </panel-item>

      <panel-item>
        <configure-relative-time-component
          id="first_trigger"
          v-model="selectedObjectModelTrigger.first_trigger"
          prop_key="first_trigger"
          :model="selectedObjectModelTrigger"
          full-width
          compress
          :unitOptions="DurationAndTimeHelper.durationUnitOptions"
          :options="startDateAndTimeOptions">
          <template>
            Start date & time
          </template>
          <help-tooltip>Please read the documentation on how this works.</help-tooltip>
        </configure-relative-time-component>
      </panel-item>

      <panel-item>
        <label :for="selectedObjectModelTrigger.idForProp('occurs_at_interval')">Repeat</label>
        <a-select
          :id="selectedObjectModelTrigger.idForProp('occurs_at_interval')"
          compress
          v-model="occursAtInterval"
          :options="objectModelRepeatOptions"
          @input="updateInterval"></a-select>

        <template v-if="selectedObjectModelTrigger.occurs_at_interval">
          <configure-interval
            :interval="selectedObjectModelTrigger.interval"
            :occurs_at_interval="selectedObjectModelTrigger.occurs_at_interval"
            :custom_interval="selectedObjectModelTrigger.custom_interval">
          </configure-interval>
        </template>
      </panel-item>

    </panel-block>

    <panel-block  v-if="selectedObjectModelTrigger.trigger_based_on_type === ObjectModelTriggerBasedOnType.ObjectModelProperty">
      <conditional-wrapper
        :parent="selectedObjectModelTrigger"
        :field-definition="ObjectModelConditionalFields.TRIGGER_AVAILABLE"
        :has-otherwise-value="false"
        :conditional-rule-options="conditionalRuleOptions"
        :can-add-rule="Object.keys(selectedObjectModelTrigger.trigger_conditional_rule_sets).length === 0"
        :validation-model="selectedObjectModelTrigger.parent"
        :validation-field-name="`triggerConditional-${selectedObjectModelTrigger.client_id}`"
      >
        <template #header>
          Triggered
        </template>
        <template #value="slotProps">
          <input type="hidden"
                 v-model="slotProps.model[slotProps.fieldName]"
                 value="true">
        </template>
      </conditional-wrapper>
    </panel-block>
  </div>
</template>


<script lang="ts">
import Vue from "vue";
import selectedMixin from "../mixins/selectedMixin";
import {ObjectModelTriggerCapability} from "~/models/objectModelTriggers/objectModelTriggerCapability";
import {ObjectModelTriggerBasedOnType} from "~/enums/objectModelTriggerBasedOnType";
import {ObjectModelTriggerRunsForType} from "~/enums/objectModelTriggerRunsForType";
import {IVOptionSetLocal} from "~/interfaces/vSelectInterfaces";
import {ObjectModelTriggerType} from "~/enums/objectModelTriggerType";
import DurationAndTimeHelper from "~/helpers/durationAndTimeHelper";
import {DurationType} from "~/enums/durationType";
import ConfigureRelativeTimeComponent from "~/components/configuration/configure-relative-time-component.vue";
import YesNoSelect from "~/components/yesNoSelect.vue"
import ConfigureInterval from "~/components/configuration/configure-interval.vue"
import ObjectModelConditionalFields from "~/models/objectModels/objectModelConditionalFields";
import ConditionalWrapper from "~/components/conditionalRuleBuilder/objectModelPropertyConditionalWrapper.vue";
import {DynamicReferenceType} from "~/enums/dynamicReferenceType";
import {IConditionalRuleOptions} from "~/interfaces/modeling/conditionalRuleInterfaces";
import {
  ObjectModelBuiltinPropertyType,
  ObjectModelBuiltinPropertyTypeName
} from "~/enums/objectModelBuiltinPropertyType";
import {DatetimeMode} from "~/models/datetimeMode";
import organizationMixin from "~/mixins/organizationMixin";
import {DurationUnit} from "~/enums/durationUnit";

export default Vue.extend({
    mixins: [selectedMixin, organizationMixin],
    components: {
      ConfigureRelativeTimeComponent,
      YesNoSelect,
      ConfigureInterval,
      ConditionalWrapper
    },
    data: function () {
      const conditionalRuleOptions: IConditionalRuleOptions = {
        showIsUpdatedComparison: true,
        showPreviousValueComparisonOptions: true,
        hideDatetimeIsWithin: true,
      }
      return {
        ObjectModelTriggerCapability,
        ObjectModelTriggerBasedOnType,
        ObjectModelTriggerRunsForType,
        DurationAndTimeHelper,
        DurationType: DurationType,
        ObjectModelConditionalFields: ObjectModelConditionalFields,
        conditionalRuleOptions,
        DatetimeMode,
      };
    },
    methods: {
      updateInterval(val) {
        if (typeof val === 'number') {
          this.selectedObjectModelTrigger.interval.unit = val;
          this.selectedObjectModelTrigger.interval.amount = 1;
          this.selectedObjectModelTrigger.occurs_at_interval = true;
          this.selectedObjectModelTrigger.custom_interval = false;
        } else if (val) {
          this.selectedObjectModelTrigger.custom_interval = true;
          this.selectedObjectModelTrigger.occurs_at_interval = true;
        } else {
          this.selectedObjectModelTrigger.occurs_at_interval = false;
        }
      },
      updateTriggerBasedOn(val) {
        if (val === ObjectModelTriggerRunsForType.Collection) {
          this.selectedObjectModelTrigger.trigger_based_on_type = ObjectModelTriggerBasedOnType.TimeBasedEvent;
          this.selectedObjectModelTrigger.first_trigger.type = DurationType.datePicker;
        } else {
          this.selectedObjectModelTrigger.first_trigger.type = DurationType.propertyBasedTimeSpan;
        }
      }
    },
    computed: {
      unitOptions() {
        return DurationAndTimeHelper.durationUnitOptions
      },
      startDateAndTimeOptions() {
        if (this.selectedObjectModelTrigger.trigger_runs_for_type === ObjectModelTriggerRunsForType.Individual && this.selectedObjectModelTrigger.can(ObjectModelTriggerCapability.triggerRunnableFor)) {
          return [
            {
              label: 'Relative Date', value: DurationType.propertyBasedTimeSpan,
              sectionConfig: {
                sectionLabel: 'Choose Date/Time or Property',
                dropdownOptions: this.relativeTimeIntervalPropertyBasedOptions
              }
            }
          ];
        } else if (this.selectedObjectModelTrigger.trigger_runs_for_type === ObjectModelTriggerRunsForType.Collection) {
          return [{label: 'Specific Date', value: DurationType.datePicker}];
        }

        return [
          {
            label: 'Relative Date',
            value: DurationType.propertyBasedTimeSpan,
            sectionConfig: {
              sectionLabel: 'Choose Date/Time or Property',
              dropdownOptions: this.relativeTimeIntervalPropertyBasedOptions
            }
          },
          {label: 'Specific Date', value: DurationType.datePicker}
        ];
      },
      objectModelTriggerBasedOnOptions() {
        if (this.selectedObjectModelTrigger.can(ObjectModelTriggerCapability.triggerRunnableFor)) {
          if (this.selectedObjectModelTrigger.trigger_runs_for_type === ObjectModelTriggerRunsForType.Collection) {
            return [{label: 'Time-based event', value: ObjectModelTriggerBasedOnType.TimeBasedEvent}];
          }
        }

        let options = [{label: 'Object Model Property', value: ObjectModelTriggerBasedOnType.ObjectModelProperty}];

        if (this.selectedObjectModelTrigger.can(ObjectModelTriggerCapability.triggerRunnableFor)) {
          options.push({label: 'Relative date', value: ObjectModelTriggerBasedOnType.TimeBasedEvent});
        } else {
          options.push({label: 'Time-based event', value: ObjectModelTriggerBasedOnType.TimeBasedEvent});
        }
        const trigger_type = this.selectedObjectModelTrigger.trigger_type;
        if (trigger_type === ObjectModelTriggerType.AutoGenerateForm || trigger_type === ObjectModelTriggerType.UpdatePropertyValues ||
          ObjectModelTriggerType.AutomaticNotification) {
          options.push({label: 'Creation', value: ObjectModelTriggerBasedOnType.Creation});

          if (trigger_type === ObjectModelTriggerType.AutomaticNotification) {
            options.push({label: "Modification", value: ObjectModelTriggerBasedOnType.Modification},
              {label: "Deletion", value: ObjectModelTriggerBasedOnType.Deletion});
          }
        }
        return options
      },
      objectModelTriggerRunsForOptions() {
        return [
          {label: 'Individual record', value: ObjectModelTriggerRunsForType.Individual},
          {label: 'Collection of records', value: ObjectModelTriggerRunsForType.Collection}
        ]
      },
      occursAtInterval() {
        if (this.selectedObjectModelTrigger.occurs_at_interval && this.selectedObjectModelTrigger.custom_interval) {
          return true;
        } else if (this.selectedObjectModelTrigger.occurs_at_interval) {
          return this.selectedObjectModelTrigger.interval.unit;
        }

        return false;
      },
      objectModelRepeatOptions() {
        return [
          {label: 'Does not repeat', value: false},
          {label: 'Daily', value: DurationUnit.day},
          {label: 'Weekly', value: DurationUnit.week},
          {label: 'Monthly', value: DurationUnit.month},
          {label: 'Yearly', value: DurationUnit.year},
          {label: 'Custom', value: true},
        ]
      },
      relativeTimeIntervalPropertyBasedOptions(): IVOptionSetLocal {
        let options = this.selectedObjectModel.dateProperties(false, true).map(x => {
          return {
            label: x.label_title,
            value: x.client_id,
            type: DynamicReferenceType.ObjectModelProperty
          };
        });

        options.push({
          label: ObjectModelBuiltinPropertyTypeName[ObjectModelBuiltinPropertyType.CreatedAt],
          value: ObjectModelBuiltinPropertyType.CreatedAt,
          type: DynamicReferenceType.ObjectModelRecordMetadata,
        });

        // this implementation will probably cause problems w/ a-select if `value` ever overlaps across different types
        return options;
      }
    }
  });
</script>